import i18next, { use as registerI18nextPlugin } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import * as z from "zod";
import zodEn from "zod/v4/locales/en.js";
import zodEs from "zod/v4/locales/es.js";
import zodFr from "zod/v4/locales/fr.js";

// Keys are ISO 639-1 codes.
const ZOD_LOCALES = { en: zodEn, es: zodEs, fr: zodFr } as const;
type SupportedZodLocale = keyof typeof ZOD_LOCALES;

const isSupportedZodLocale = (lng: string): lng is SupportedZodLocale =>
    lng in ZOD_LOCALES;

const loadZodLocale = (language: string): void => {
    const target = isSupportedZodLocale(language) ? language : "en";
    z.config(ZOD_LOCALES[target]());
};

// Skipped when already initialised: vitest-setup.ts initialises i18next (fs
// backend) before the app modules load, and re-initialising it would swap in
// the HTTP backend, which cannot load locales under Node.
// Keep this config in sync with the i18n.init inside src/vitest-setup.ts
if (!i18next.isInitialized) {
    await registerI18nextPlugin(Backend)
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
            // This allows some resources (or no resources)
            // to be set on initialization while others can be loaded using a backend connector.
            partialBundledLanguages: true,
            load: "languageOnly", // Search only for the language en instead of en-US
            debug: import.meta.env.MODE === "development",
            fallbackLng: "en",
            supportedLngs: ["en", "es", "fr"],
            ns: [
                "agent",
                "campaign",
                "common",
                "component",
                "error",
                "private",
                "public",
                "station",
                "validation",
            ],
            defaultNS: "public",
            interpolation: {
                escapeValue: false,
            },
        })
        .then(null, (error) => {
            console.error("i18n failed to initialize", error);
        });

    // Load initial Zod locale based on detected language,
    // before any validation runs in the app.
    loadZodLocale(i18next.language);

    // Update Zod locale when i18next language changes
    i18next.on("languageChanged", (lng: string) => {
        loadZodLocale(lng);
    });
}

export default i18next;
